import { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  ChevronRight,
  Lightbulb,
  Play,
  Eye,
  Globe,
  ChevronDown,
} from "lucide-react";
import { debounce } from "lodash";
import { Link, useNavigate } from "react-router-dom";

import BaseAnchor from "@/components_v2/Anchor/BaseAnchor";
import BaseAccordion from "@/components_v2/Accordion/BaseAccordion";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseDropdownButton from "@/components_v2/Button/BaseDropdownButton";
import BaseCheckbox from "@/components_v2/FormFields/Checkbox/BaseCheckbox";
import InfoCard from "@/components_v2/Card/InfoCard";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import BaseDivider from "@/components_v2/Divider/BaseDivider";
import { BUTTON_VARIANTS } from "@/components_v2/Button/constants";
import {
  useGetSurveyChecklistQuery,
  useGetSurveySamplesQuery,
  useUpdateSurveyChecklistMutation,
} from "@/redux/service";
import {
  CHECKLIST_LABEL_LINKS,
  GuidancePageProps,
  SipPreviewMap,
} from "@/features/survey/guidance/types";
import {
  ABOUT_THE_SIP_CARDS,
  CALENDAR_EVENTS_ITEMS,
  CALENDAR_ITEMS,
  FAQ_ITEMS,
  GUIDANCE_PAGE_ANCHOR_ITEMS,
  NEW_FOR_2026_CARDS,
  PREPARATION_CHECKLIST_ACCORDIONS,
} from "./constants";
import CalendarCard from "./components/CalendarCard";
import EventCard from "./components/EventCard";
import SampleSurveyCard from "./components/SampleSurveyCard";
import TestimonialCard from "./components/TestimonialCard";
import {
  SIP_TUTORIAL_URL,
  SipPreviews,
  SurveyType,
} from "@/shared/utils/constants";
import { getGuidancePrefixForType, getGuidanceTranslator } from "./utils";
import { SurveySampleResponse } from "@/redux/survey/types";

import "./styles.css";

const getSampleSurveyLink = (
  sampleSource: string | number | null,
  sample: SurveySampleResponse,
) => {
  const source = sampleSource ?? "";
  const reportPath = `/reports/${source}/overview?organization=${sample.organization.id}`;
  const reportViewPath = sample.reportViewUuid
    ? `${window.location.origin}/public/reports/views/${sample.reportViewUuid}`
    : "";

  return { reportPath, reportViewPath };
};

const GuidancePage = ({ survey }: GuidancePageProps) => {
  const { t } = useTranslation();
  const guidanceTranslator = getGuidanceTranslator(survey.type);
  const navigate = useNavigate();

  const { data: surveyOrganizationSamples } = useGetSurveySamplesQuery(
    survey.id,
  );
  const { data: checklistData } = useGetSurveyChecklistQuery(survey.details.id);
  const [updateChecklist] = useUpdateSurveyChecklistMutation();
  const [checklistChecked, setChecklistChecked] = useState<
    Record<string, boolean>
  >({});
  const [selectedLanguage, setSelectedLanguage] = useState(
    SipPreviews.EN.value,
  );
  const guidancePrefix = getGuidancePrefixForType(survey.type);
  const sipPreviewLink =
    (SipPreviews as unknown as SipPreviewMap)[selectedLanguage]?.link ??
    SipPreviews.EN.link;

  const debouncedSave = useRef(
    debounce((state: Record<string, boolean>) => {
      updateChecklist({
        assignmentId: survey.details.id,
        data: state,
      });
    }, 800),
  ).current;

  useEffect(() => {
    if (checklistData) {
      setChecklistChecked(checklistData);
    }
  }, [checklistData]);

  useEffect(() => {
    return () => debouncedSave.cancel();
  }, [debouncedSave]);

  const handleCheckChange = useCallback(
    (id: string, checked: boolean) => {
      setChecklistChecked((prev) => {
        const next = { ...prev, [id]: checked };
        debouncedSave(next);
        return next;
      });
    },
    [debouncedSave],
  );

  const handleDownloadPreview = () => {
    const link =
      (SipPreviews as unknown as SipPreviewMap)[selectedLanguage]?.link ??
      SipPreviews.EN.link;
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const sipPreviewMenuProps = {
    items: SipPreviews.asList().map(
      (opt: { value: string; label: string }) => ({
        key: opt.value,
        label: opt.label,
        onClick: () => setSelectedLanguage(opt.value),
      }),
    ),
  };

  const checklistAccordionItems = PREPARATION_CHECKLIST_ACCORDIONS.map(
    ({ key: accordionKey, icon: Icon, titleKey, items }) => ({
      key: accordionKey,
      label: (
        <div className="items-center justify-start gap-300">
          <span className="checklist-accordion-icon">
            <Icon size={20} />
          </span>
          <span className="checklist-accordion-title body-m-medium">
            {guidanceTranslator(titleKey)}
          </span>
        </div>
      ),
      children: (
        <div className="flex flex-col gap-400">
          <div className="guidance-page-checklist-content-list flex flex-col gap-200 body-m-regular">
            {items.map(({ id, labelKey, descriptionKeys, labelLink }) => {
              const fullLabelKey = `${guidancePrefix}.${labelKey}`;
              const label = labelLink ? (
                <Trans
                  i18nKey={fullLabelKey}
                  components={{
                    [`${labelLink}Link`]: (
                      <Link
                        to={
                          labelLink === CHECKLIST_LABEL_LINKS.PREVIEW
                            ? sipPreviewLink
                            : SIP_TUTORIAL_URL
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="guidance-page-checklist-link"
                      />
                    ),
                  }}
                />
              ) : (
                guidanceTranslator(labelKey)
              );
              return (
                <div key={id}>
                  <BaseCheckbox
                    label={label}
                    checked={checklistChecked[id] ?? false}
                    className="body-m-regular"
                    onChange={(checked) => handleCheckChange(id, checked)}
                  />
                  {descriptionKeys?.map((key) => (
                    <p
                      key={key}
                      className="guidance-page-section-description-list-item body-s-regular"
                    >
                      {guidanceTranslator(key)}
                    </p>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      ),
    }),
  );

  const faqAccordionItems = FAQ_ITEMS.map(
    ({ key: itemKey, questionKey, answerKey }) => ({
      key: itemKey,
      label: (
        <span className="guidance-page-faq-question body-m-medium">
          {guidanceTranslator(questionKey)}
        </span>
      ),
      children: (
        <p className="guidance-page-section-description body-m-regular">
          {guidanceTranslator(answerKey)}
        </p>
      ),
    }),
  );

  const anchorItems = GUIDANCE_PAGE_ANCHOR_ITEMS.map(
    ({ key, href, titleKey, icon: Icon }) => ({
      key,
      href,
      title: (
        <span className="guidance-page-anchor-item body-m-medium flex items-center gap-200">
          <BaseSvgRenderer component={Icon} width="20" height="20" />
          {guidanceTranslator(titleKey)}
        </span>
      ),
    }),
  );

  return (
    <div className="guidance-page flex full-width justify-self-center gap-500">
      <aside
        className="guidance-page-panel flex-shrink-0"
        aria-label={t("survey.labels.guidancePage")}
        data-samples-count={surveyOrganizationSamples?.length}
      >
        <BaseAnchor
          className="guidance-page-anchor"
          items={anchorItems}
          offsetTop={100}
          urlSync
        />
      </aside>

      <main className="guidance-page-content flex flex-col flex-1 gap-600">
        <section id="about" className="flex flex-col gap-300">
          <h2 className="header-l-medium">
            {guidanceTranslator("preparingTitle")}
          </h2>

          <p className="guidance-page-section-description body-m-regular">
            {guidanceTranslator("preparingDescription")}
          </p>

          <div className="flex flex-wrap gap-200 items-center">
            <BaseButton
              variant={BUTTON_VARIANTS.PRIMARY}
              trailingIcon={
                <BaseSvgRenderer
                  component={ChevronRight}
                  width="20"
                  height="20"
                />
              }
              onClick={() => navigate(`/reports/${survey.id}`)}
            >
              {guidanceTranslator("startYourSurvey")}
            </BaseButton>

            <BaseDropdownButton
              className="guidance-page-download-preview-button"
              menu={sipPreviewMenuProps}
              onClick={handleDownloadPreview}
              icon={
                <div className="flex items-center gap-200">
                  <Globe size={16} />
                  <ChevronDown size={20} />
                </div>
              }
            >
              {t("downloadSurvey", {
                surveyTitle: survey?.title || SurveyType.SIP.value,
              })}{" "}
              ({SipPreviews.getLabel(selectedLanguage)})
            </BaseDropdownButton>

            <BaseButton
              variant={BUTTON_VARIANTS.TERTIARY}
              leadingIcon={
                <BaseSvgRenderer component={Play} width="20" height="20" />
              }
              onClick={() => window.open(SIP_TUTORIAL_URL, "_blank")}
              target="_blank"
            >
              {guidanceTranslator("viewSurveyTutorial")}
            </BaseButton>
          </div>
        </section>

        <BaseDivider />

        <section id="about" className="flex flex-col gap-400">
          <h2 className="header-m-medium">
            {guidanceTranslator("aboutTheSurvey")}
          </h2>

          <div className="flex flex-col gap-200">
            <p className="guidance-page-section-description body-m-regular">
              {guidanceTranslator("aboutTheSurveyDescription1")}
            </p>

            <p className="guidance-page-section-description body-m-regular">
              {guidanceTranslator("aboutTheSurveyDescription2")}
            </p>
          </div>

          <div className="guidance-page-about-cards gap-300">
            {ABOUT_THE_SIP_CARDS.map(
              ({ key, icon: Icon, titleKey, descriptionKey }) => (
                <InfoCard
                  key={key}
                  icon={<Icon size={24} />}
                  title={guidanceTranslator(titleKey)}
                  description={guidanceTranslator(descriptionKey)}
                />
              ),
            )}
          </div>

          <div className="flex flex-col gap-200">
            <p className="guidance-page-section-description header-s-medium">
              {guidanceTranslator("aboutTheSurveySubtitle")}
            </p>

            <p className="guidance-page-section-description body-m-regular">
              {guidanceTranslator("aboutTheSurveyDescription3")}
            </p>

            <p className="guidance-page-section-description body-m-regular">
              {guidanceTranslator("aboutTheSurveyDescription4")}
            </p>

            <p className="guidance-page-section-description body-m-regular">
              {guidanceTranslator("aboutTheSurveyDescription5")}
            </p>
          </div>
        </section>

        <BaseDivider />

        <section id="tool-for-impact" className="flex flex-col gap-400">
          <h2 className="header-m-medium">
            {guidanceTranslator("toolForImpact")}
          </h2>

          <p className="guidance-page-section-description body-m-regular">
            {guidanceTranslator("toolForImpactDescription")}
          </p>

          <div className="flex flex-col gap-200">
            <p className="guidance-page-section-description header-s-medium">
              {guidanceTranslator("toolForImpactSubtitle1")}
            </p>

            <p className="guidance-page-section-description body-m-regular">
              {guidanceTranslator("toolForImpactDescription1")}
            </p>

            <a
              className="body-m-regular"
              href="https://d30mzt1bxg5llt.cloudfront.net/public/uploads/PDFs/Student-Engagement-combined.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {guidanceTranslator("toolForImpactLink")}
            </a>
          </div>

          <div className="flex flex-col gap-200">
            <p className="guidance-page-section-description header-s-medium">
              {guidanceTranslator("toolForImpactSubtitle2")}
            </p>

            <p className="guidance-page-section-description body-m-regular">
              {guidanceTranslator("toolForImpactDescription2")}
            </p>
          </div>

          <div className="flex flex-col gap-200">
            <p className="guidance-page-section-description header-s-medium">
              {guidanceTranslator("toolForImpactSubtitle3")}
            </p>

            <p className="guidance-page-section-description body-m-regular">
              {guidanceTranslator("toolForImpactDescription3")}
            </p>

            <ol className="guidance-page-numbered-list flex flex-col gap-200">
              {[...Array(5)].map((_, i) => (
                <li key={i} className="body-s-regular">
                  <div className="guidance-page-numbered-list-content">
                    <p className="guidance-page-section-list-title body-m-medium">
                      {guidanceTranslator(
                        `toolForImpactList1Item${i + 1}Title`,
                      )}
                    </p>
                    <p className="guidance-page-section-list-description body-m-regular">
                      {guidanceTranslator(
                        `toolForImpactList1Item${i + 1}Description`,
                      )}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="flex flex-col gap-400">
            <p className="guidance-page-section-description header-s-medium">
              {guidanceTranslator("toolForImpactSubtitle4")}
            </p>

            <div className="guidance-page-testimonial-cards">
              {[...Array(10)].map((_, i) => (
                <TestimonialCard
                  key={i}
                  stepNumber={i + 1}
                  title={guidanceTranslator(
                    `toolForImpactList2Item${i + 1}Title`,
                  )}
                  description={guidanceTranslator(
                    `toolForImpactList2Item${i + 1}Description`,
                  )}
                  attribution={guidanceTranslator(
                    `toolForImpactList2Item${i + 1}Attribution`,
                  )}
                />
              ))}
            </div>
          </div>
        </section>

        <BaseDivider />

        <section id="new-for-2026" className="flex flex-col gap-400">
          <h2 className="header-m-medium">
            {guidanceTranslator("newFor2026")}
          </h2>

          <p className="guidance-page-section-description body-m-regular">
            {guidanceTranslator("newFor2026Description")}
          </p>

          <BaseAccordion
            className="guidance-page-new-for-2026"
            expandIconPosition="end"
            items={NEW_FOR_2026_CARDS.map(
              ({ key, icon: Icon, titleKey, descriptionKey }) => ({
                key,
                label: (
                  <div className="flex items-center gap-300 justify-start">
                    <Icon size={20} />

                    <span className="checklist-accordion-title body-m-medium">
                      {guidanceTranslator(titleKey)}
                    </span>
                  </div>
                ),
                children: (
                  <p className="guidance-page-section-description body-m-regular">
                    {guidanceTranslator(descriptionKey)}
                  </p>
                ),
              }),
            )}
          />
        </section>

        <BaseDivider />

        <section id="checklist" className="flex flex-col gap-400">
          <h2 className="header-m-medium">
            {guidanceTranslator("preparationChecklistTitle")}
          </h2>

          <p className="guidance-page-section-description body-m-regular">
            {guidanceTranslator("preparationChecklistSubtitle")}
          </p>

          <BaseAccordion
            className="guidance-page-checklist"
            expandIconPosition="end"
            items={checklistAccordionItems}
          />
        </section>

        <BaseDivider />

        <section id="exemplary-sips" className="flex flex-col gap-400">
          <h2 className="header-m-medium">
            {guidanceTranslator("exemplarySurveysTitle")}
          </h2>

          <p className="guidance-page-section-description guidance-page-exemplary-sips-intro body-m-regular">
            {guidanceTranslator("exemplarySurveysIntro")}
          </p>

          {surveyOrganizationSamples && (
            <div className="guidance-page-exemplary-cards">
              {surveyOrganizationSamples.map((sample) => {
                const { reportPath, reportViewPath } = getSampleSurveyLink(
                  survey.sampleSource,
                  sample,
                );
                return (
                  <SampleSurveyCard
                    key={sample.organization.id}
                    title={sample.organization.name}
                    reportPath={reportPath}
                    reportViewPath={reportViewPath}
                    surveyType={survey.type}
                  />
                );
              })}
            </div>
          )}
        </section>

        <BaseDivider />

        <section id="how-to-complete" className="flex flex-col gap-400">
          <h2 className="header-m-medium">
            {guidanceTranslator("howToCompleteTheSurvey")}
          </h2>

          <p className="guidance-page-section-description body-m-regular">
            {guidanceTranslator("howToCompleteDescription")}
          </p>

          <div className="guidance-page-tips-card flex flex-col gap-300">
            <div className="flex items-center gap-200">
              <Lightbulb size={20} />

              <h3 className="guidance-page-tips-card-title body-m-medium">
                {guidanceTranslator("howToCompleteTipsTitle")}
              </h3>
            </div>

            <div className="guidance-page-tips-card-columns">
              <ul className="guidance-page-tips-card-columns-list body-m-regular">
                {[
                  "howToCompleteTip1",
                  "howToCompleteTip2",
                  "howToCompleteTip3",
                ].map((key) => (
                  <li
                    className="guidance-page-tips-card-columns-list-item"
                    key={key}
                  >
                    {guidanceTranslator(key)}
                  </li>
                ))}
              </ul>

              <ul className="guidance-page-tips-card-columns-list body-m-regular">
                {[
                  "howToCompleteTip4",
                  "howToCompleteTip5",
                  "howToCompleteTip6",
                ].map((key) => (
                  <li
                    className="guidance-page-tips-card-columns-list-item"
                    key={key}
                  >
                    {guidanceTranslator(key)}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="guidance-page-tutorial-card flex flex-col items-center gap-300">
            <span className="guidance-page-tutorial-card-icon flex items-center justify-center">
              <Eye size={24} />
            </span>

            <h3 className="guidance-page-tutorial-card-title header-m-medium text-center">
              {guidanceTranslator("howToCompleteTutorialTitle")}
            </h3>

            <p className="guidance-page-section-description body-m-regular text-center">
              {guidanceTranslator("howToCompleteTutorialDescription")}
            </p>

            <BaseButton
              variant={BUTTON_VARIANTS.PRIMARY}
              onClick={() => window.open(SIP_TUTORIAL_URL, "_blank")}
              trailingIcon={
                <BaseSvgRenderer
                  component={ChevronRight}
                  width="20"
                  height="20"
                />
              }
            >
              {guidanceTranslator("howToCompleteTutorialButton")}
            </BaseButton>
          </div>
        </section>

        <BaseDivider />

        <section id="faq" className="flex flex-col gap-400">
          <h2 className="header-m-medium">{guidanceTranslator("faqTitle")}</h2>

          <p className="guidance-page-section-description body-m-regular">
            {guidanceTranslator("faqSubtitle")}
          </p>

          <BaseAccordion
            className="guidance-page-faq"
            expandIconPosition="end"
            items={faqAccordionItems}
          />
        </section>

        <BaseDivider />

        <section id="calendar" className="flex flex-col gap-400">
          <h2 className="header-l-medium">{guidanceTranslator("calendar")}</h2>

          <p className="guidance-page-section-description body-m-regular">
            {guidanceTranslator("calendarSubtitle")}
          </p>

          <div className="flex flex-col gap-300">
            <p className="guidance-page-section-description body-m-regular">
              {guidanceTranslator("calendarDeadlinesSubtitle")}
            </p>

            <div className="flex flex-col gap-300">
              {CALENDAR_ITEMS.map(
                ({
                  key: itemKey,
                  dateKey,
                  titleKey,
                  descriptionKey,
                  tagKey,
                  linkUrl,
                  linkLabelKey,
                }) => (
                  <CalendarCard
                    key={itemKey}
                    date={guidanceTranslator(dateKey)}
                    title={guidanceTranslator(titleKey)}
                    description={guidanceTranslator(descriptionKey)}
                    tag={tagKey ? guidanceTranslator(tagKey) : undefined}
                    linkUrl={linkUrl}
                    linkLabel={
                      linkLabelKey
                        ? guidanceTranslator(linkLabelKey)
                        : undefined
                    }
                  />
                ),
              )}
            </div>
          </div>

          <div className="flex flex-col gap-300">
            <p className="guidance-page-section-description body-m-regular">
              {guidanceTranslator("calendarEventsSubtitle")}
            </p>

            <div className="guidance-page-calendar-events-cards gap-300">
              {CALENDAR_EVENTS_ITEMS.map(
                ({
                  key: itemKey,
                  dateKey,
                  timeKey,
                  titleKey,
                  descriptionKey,
                  isCompleted,
                  linkUrl,
                }) => (
                  <EventCard
                    key={itemKey}
                    date={guidanceTranslator(dateKey)}
                    time={guidanceTranslator(timeKey)}
                    title={guidanceTranslator(titleKey)}
                    description={guidanceTranslator(descriptionKey)}
                    isCompleted={isCompleted}
                    linkUrl={linkUrl}
                  />
                ),
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default GuidancePage;
